import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { Pill } from '@/components/pills/Pill'

import { fetchRun } from '@/reducers/costimizer/costimizer.actions'
import {
  ACTIVE_STATUSES,
  POLLABLE_STATUSES,
  RUN_STATUS,
  selectRun,
} from '@/reducers/costimizer/costimizer.redux'

import { StartMigrationButton } from './StartMigrationButton'

const POLL_MS = 20000

const STATUS_LABEL = {
  [RUN_STATUS.STARTING]: 'Starting…',
  [RUN_STATUS.QUEUED]: 'Queued',
  [RUN_STATUS.RUNNING]: 'Running',
  [RUN_STATUS.DONE]: 'Results ready',
  [RUN_STATUS.FAILED]: 'Failed',
  [RUN_STATUS.CANCELLED]: 'Cancelled',
}

/**
 * The migrate button, or the state of the run already going. Polls while a run is live.
 *
 * Used both in the scorecard list and on a single scorecard's page, which is why it owns the polling rather
 * than either caller.
 *
 * @param {object} props.serverRun the run the server reported, or nulls when there has never been one
 */
export const MigrationCell = ({
  scorecardConfigId,
  fullyMigrated,
  nMigratable,
  perPromptMinutes,
  serverRun,
}) => {
  const dispatch = useDispatch()
  const run = useSelector(selectRun(scorecardConfigId))

  // The server's status wins until this cell has one of its own — otherwise a fresh page load would show no
  // run for a scorecard that has been going for twenty minutes.
  const status = run.status ?? serverRun.status

  useEffect(() => {
    if (!POLLABLE_STATUSES.includes(status)) {
      return undefined
    }

    const timer = setInterval(() => dispatch(fetchRun(scorecardConfigId)), POLL_MS)

    return () => clearInterval(timer)
  }, [dispatch, scorecardConfigId, status])

  // No button, because there is nothing left to test — and no "Nothing to migrate" pill either: the list
  // already says "All migrated" in the column about what there is to migrate. A finished run's state still
  // shows, since its results are still worth reading.
  if (fullyMigrated) {
    if (isNil(status)) {
      return null
    }

    return <Pill dataTestId="costimizer-run-status">{STATUS_LABEL[status]}</Pill>
  }

  // ⚠ ONLY while a run is IN FLIGHT. `done`, `failed` and `cancelled` fall through to the button below: a
  // finished run is not a reason to refuse another one, and the server's own guard
  // (`service.AlreadyRunning`) blocks only `queued` and `running`.
  if (ACTIVE_STATUSES.includes(status)) {
    return <Pill dataTestId="costimizer-run-status">{STATUS_LABEL[status]}</Pill>
  }

  return (
    <span className="flex-align-center small-gap">
      {!isNil(status) && <Pill dataTestId="costimizer-run-status">{STATUS_LABEL[status]}</Pill>}
      <StartMigrationButton
        brand
        scorecardConfigId={scorecardConfigId}
        nSubjects={nMigratable}
        perPromptMinutes={perPromptMinutes}
        // Says which it is, since a finished run leaves its status pill sitting right beside the button.
        label={isNil(status) ? 'Test migration' : 'Re-test scorecard'}
      />
    </span>
  )
}
